import { DEFAULT_PAGE_SIZE, listSearch, PAGE_SIZES, type Page, type SortDir } from '@cyberscore/contracts';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { api } from './api';
import { hashQuery, navigate, useHash } from './router';
import { useSession } from './session';

/**
 * A list's state (task 60): search, filters, sort, page and view. It lives in the address after the
 * route (#/clients?q=kinta&page=2), so back, reload and a shared link all show the same page; the server
 * does the searching, filtering, sorting and paging.
 */

export type View = 'cards' | 'table';

export interface ListSpec {
  /** Remembers the card or table choice per list on this device. */
  id: string;
  sort: string;
  dir?: SortDir;
  filters?: readonly string[];
  /** Lists with cards start on cards unless the person chose the table. */
  view?: View;
  pageSize?: number;
}

export interface ListState {
  q: string;
  sort: string;
  dir: SortDir;
  page: number;
  page_size: number;
  view: View;
  filters: Record<string, string>;
}

const VIEW_KEY = 'cyberscore.view.';
const readView = (id: string): View | null => {
  try {
    const v = localStorage.getItem(VIEW_KEY + id);
    return v === 'cards' || v === 'table' ? v : null;
  } catch {
    return null;
  }
};

export function readList(query: URLSearchParams, spec: ListSpec): ListState {
  const num = (k: string, d: number) => {
    const n = Number(query.get(k));
    return Number.isInteger(n) && n > 0 ? n : d;
  };
  const size = num('page_size', spec.pageSize ?? DEFAULT_PAGE_SIZE);
  const view = query.get('view');
  return {
    q: query.get('q') ?? '',
    sort: query.get('sort') || spec.sort,
    dir: query.get('dir') === 'asc' ? 'asc' : query.get('dir') === 'desc' ? 'desc' : (spec.dir ?? 'desc'),
    page: num('page', 1),
    page_size: (PAGE_SIZES as readonly number[]).includes(size) ? size : DEFAULT_PAGE_SIZE,
    view: view === 'cards' || view === 'table' ? view : (readView(spec.id) ?? spec.view ?? 'table'),
    filters: Object.fromEntries((spec.filters ?? []).map((f) => [f, query.get(f) ?? ''])),
  };
}

/** The query string the server gets: defaults left out. */
export const apiSearch = (s: ListState) =>
  listSearch({ q: s.q.trim(), sort: s.sort, dir: s.dir, page: s.page, page_size: s.page_size, ...s.filters });

export function useListState(spec: ListSpec) {
  const hash = useHash();
  const [path] = hash.split('?');
  const state = useMemo(() => readList(hashQuery(hash), spec), [hash, spec]);

  const set = useCallback(
    (patch: Partial<Omit<ListState, 'filters'>> & { filters?: Record<string, string> }) => {
      const next: ListState = { ...state, ...patch, filters: { ...state.filters, ...patch.filters } };
      // Anything but turning the page starts again at the first page.
      if (!('page' in patch)) next.page = 1;
      if (patch.view) {
        try {
          localStorage.setItem(VIEW_KEY + spec.id, patch.view);
        } catch {
          /* storage unavailable: the choice lasts this visit */
        }
      }
      const q = listSearch({
        q: next.q,
        sort: next.sort === spec.sort ? undefined : next.sort,
        dir: next.dir === (spec.dir ?? 'desc') ? undefined : next.dir,
        page: next.page === 1 ? undefined : next.page,
        page_size: next.page_size === (spec.pageSize ?? DEFAULT_PAGE_SIZE) ? undefined : next.page_size,
        view: next.view,
        ...next.filters,
      });
      // What the page around the list keeps in the address (a record's open tab, say) stays.
      const own = new Set(['q', 'sort', 'dir', 'page', 'page_size', 'view', ...(spec.filters ?? [])]);
      const kept = new URLSearchParams(
        [...hashQuery(hash).entries()].filter(([k]) => !own.has(k)),
      ).toString();
      const search = kept ? (q ? `?${kept}&${q.slice(1)}` : `?${kept}`) : q;
      // Typing a search replaces the entry; everything else can be gone back to.
      navigate(`${path || '#/'}${search}`, { replace: 'q' in patch });
    },
    [state, path, spec, hash],
  );
  const clear = useCallback(
    () => set({ q: '', filters: Object.fromEntries(Object.keys(state.filters).map((k) => [k, ''])) }),
    [set, state.filters],
  );
  const filtered = !!state.q.trim() || Object.values(state.filters).some(Boolean);
  return { state, set, clear, filtered };
}

/**
 * Asks every list again after a change. A list still on its first load would otherwise keep that load,
 * which may have left before the change, so loads in flight are dropped first.
 */
export async function refreshLists(qc: QueryClient) {
  await qc.cancelQueries({ queryKey: ['list'] });
  return qc.invalidateQueries({ queryKey: ['list'] });
}

/** One page from a list endpoint, keeping the last page on screen while the next loads. */
export function usePage<T, X = object>(path: string, state: ListState, extra = '') {
  const { tenant } = useSession();
  const search = apiSearch(state);
  return useQuery({
    queryKey: ['list', tenant?.id, path, search, extra],
    queryFn: () =>
      api<Page<T> & X>('GET', `${path}${search}${extra && (search ? `&${extra}` : `?${extra}`)}`),
    placeholderData: keepPreviousData,
    // Lists change as people work elsewhere: coming back to one always asks again.
    staleTime: 0,
    enabled: !!tenant,
  });
}
